package com.arena_web.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document
import kotlinx.browser.window

object SeoHelper {
    // Canonical host — fixed so www.* and dev hosts all point Google at one origin.
    private const val SITE_ORIGIN = "https://arenaproplay.com"

    // Short brand shown in the browser tab. The full SEO title stays in the
    // DOM until the first real input event — crawlers never send one, so
    // Google indexes the long title while people see just "Arena".
    private const val TAB_TITLE = "Arena"
    private var userInteracted = false
    private var interactionHookInstalled = false

    private fun installInteractionHook() {
        if (interactionHookInstalled) return
        interactionHookInstalled = true
        val events = listOf("pointerdown", "mousemove", "touchstart", "keydown", "wheel")
        val onFirstInput: (org.w3c.dom.events.Event) -> Unit = {
            if (!userInteracted) {
                userInteracted = true
                document.title = TAB_TITLE
            }
        }
        events.forEach { window.addEventListener(it, onFirstInput, js("{ once: true, passive: true }")) }
    }

    private fun setMetaTag(name: String, content: String, isProperty: Boolean = false) {
        val selector = if (isProperty) "meta[property='$name']" else "meta[name='$name']"
        val element = document.querySelector(selector)
        if (element != null) {
            element.setAttribute("content", content)
        } else {
            val meta = document.createElement("meta")
            if (isProperty) {
                meta.setAttribute("property", name)
            } else {
                meta.setAttribute("name", name)
            }
            meta.setAttribute("content", content)
            document.head?.appendChild(meta)
        }
    }

    private fun setCanonical(url: String) {
        val existing = document.querySelector("link[rel='canonical']")
        if (existing != null) {
            existing.setAttribute("href", url)
        } else {
            val link = document.createElement("link")
            link.setAttribute("rel", "canonical")
            link.setAttribute("href", url)
            document.head?.appendChild(link)
        }
    }

    fun updatePageMeta(
        title: String,
        description: String = "",
        ogImage: String = "/og-image.png",
        ogType: String = "website"
    ) {
        installInteractionHook()
        document.title = if (userInteracted) TAB_TITLE else title

        if (description.isNotBlank()) {
            setMetaTag("description", description)
        }

        // Canonical + og:url follow the SPA route on the canonical host.
        val canonicalUrl = SITE_ORIGIN + window.location.pathname
        setCanonical(canonicalUrl)
        setMetaTag("og:url", canonicalUrl, isProperty = true)

        setMetaTag("og:title", title, isProperty = true)
        setMetaTag("og:type", ogType, isProperty = true)
        if (description.isNotBlank()) {
            setMetaTag("og:description", description, isProperty = true)
        }
        setMetaTag("og:image", if (ogImage.startsWith("http")) ogImage else SITE_ORIGIN + ogImage, isProperty = true)

        setMetaTag("twitter:title", title)
        if (description.isNotBlank()) {
            setMetaTag("twitter:description", description)
        }
    }
}

@Composable
fun PageSeo(
    title: String,
    description: String = "",
    ogImage: String = "/og-image.png"
) {
    LaunchedEffect(title, description) {
        SeoHelper.updatePageMeta(
            title = title,
            description = description,
            ogImage = ogImage
        )
    }
}
